import LecturerLayout from '../../components/LecturerLayout';
import { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import axiosInstance from '../../utils/axiosInstance';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    LineChart,
    Line,
} from 'recharts';

export default function SupportTask() {
    const [tasks, setTasks] = useState([]);
    const [taskCategories, setTaskCategories] = useState([]);
    const [stats, setStats] = useState({
        total_tasks: 0,
        total_hours: 0,
        avg_hours_per_task: 0,
        tasks_this_month: 0,
        hours_this_month: 0,
    });
    const [monthlyStats, setMonthlyStats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingTask, setEditingTask] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const [formData, setFormData] = useState({
        subject: '',
        description: '',
        category: '',
        date: new Date().toISOString().split('T')[0],
        start_time: '',
        end_time: '',
    });

    useEffect(() => {
        fetchSupportTaskContext();
    }, []);

    useEffect(() => {
        fetchTasks();
        fetchMonthlyStats();
    }, [selectedCategory, startDate, endDate]);

    const fetchSupportTaskContext = async () => {
        try {
            const response = await axiosInstance.get('/lecturer/support-task');
            const data = response?.data || {};
            setTaskCategories(data.taskCategories || []);
        } catch (error) {
            console.error('Error fetching support task context:', error);
        }
    };

    const fetchTasks = async () => {
        try {
            setLoading(true);
            const params = {};
            if (selectedCategory) params.category = selectedCategory;
            if (startDate) params.start_date = startDate;
            if (endDate) params.end_date = endDate;

            const { data } = await axiosInstance.get('/admin/completed-tasks', { params });

            if (data?.success) {
                setTasks(data.tasks || []);
                setStats(data.stats || {
                    total_tasks: 0,
                    total_hours: 0,
                    avg_hours_per_task: 0,
                    tasks_this_month: 0,
                    hours_this_month: 0,
                });
            } else if (data && data.message) {
                Swal.fire('Error', data.message, 'error');
            }
        } catch (error) {
            console.error('Error fetching tasks:', error);
            const message = error?.response?.data?.message || 'Failed to fetch tasks';
            Swal.fire('Error', message, 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchMonthlyStats = async () => {
        try {
            const year = new Date().getFullYear();
            const params = { year };
            if (selectedCategory) params.category = selectedCategory;
            if (startDate) params.start_date = startDate;
            if (endDate) params.end_date = endDate;

            const { data } = await axiosInstance.get('/admin/completed-tasks/monthly-stats', {
                params,
            });

            if (data?.success) {
                setMonthlyStats(data.monthly_stats || []);
            }
        } catch (error) {
            console.error('Error fetching monthly stats:', error);
        }
    };

    const handleSubmit = async e => {
        e.preventDefault();

        try {
            const url = editingTask
                ? `/admin/completed-tasks/${editingTask.id}`
                : '/admin/completed-tasks';

            const method = editingTask ? 'put' : 'post';

            const { data } = await axiosInstance[method](url, formData);

            if (data?.success) {
                Swal.fire('Success', data.message, 'success');
                setShowForm(false);
                setEditingTask(null);
                resetForm();
                fetchTasks();
                fetchMonthlyStats();
            } else {
                Swal.fire('Error', data?.message || 'Failed to save task', 'error');
            }
        } catch (error) {
            console.error('Error saving task:', error);
            const message = error?.response?.data?.message || 'Failed to save task';
            Swal.fire('Error', message, 'error');
        }
    };

    const handleEdit = task => {
        setEditingTask(task);
        setFormData({
            subject: task.subject,
            description: task.description || '',
            category: task.category,
            date: task.date,
            start_time: task.start_time,
            end_time: task.end_time,
        });
        setShowForm(true);
    };

    const handleDelete = async id => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#EF4444',
            cancelButtonColor: '#6B7280',
            confirmButtonText: 'Yes, delete it!',
        });

        if (result.isConfirmed) {
            try {
                const { data } = await axiosInstance.delete(`/admin/completed-tasks/${id}`);

                if (data?.success) {
                    Swal.fire('Deleted!', data.message, 'success');
                    fetchTasks();
                    fetchMonthlyStats();
                } else {
                    Swal.fire('Error', data?.message || 'Failed to delete task', 'error');
                }
            } catch (error) {
                console.error('Error deleting task:', error);
                const message = error?.response?.data?.message || 'Failed to delete task';
                Swal.fire('Error', message, 'error');
            }
        }
    };

    const resetForm = () => {
        setFormData({
            subject: '',
            description: '',
            category: '',
            date: new Date().toISOString().split('T')[0],
            start_time: '',
            end_time: '',
        });
    };

    const getCategoryColor = category => {
        const cat = taskCategories.find(c => c.name === category);
        return cat?.color || '#64748B';
    };

    // Prepare chart data
    const categoryData = taskCategories.map(cat => {
        const categoryTasks = tasks.filter(t => t.category === cat.name);
        const totalHours = categoryTasks.reduce((sum, t) => sum + parseFloat(t.duration.toString()), 0);
        return {
            name: cat.name,
            tasks: categoryTasks.length,
            hours: parseFloat(totalHours.toFixed(2)),
            color: cat.color,
        };
    }).filter(d => d.tasks > 0);

    return (
        <LecturerLayout title="Support Task">
            <div className="mx-auto max-w-7xl space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">Support Task Tracking</h1>
                        <p className="mt-1 text-sm text-gray-600">
                            Track your non-lecturing hours to improve productivity
                        </p>
                    </div>
                    <button
                        onClick={() => {
                            setShowForm(true);
                            setEditingTask(null);
                            resetForm();
                        }}
                        className="inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-md transition-colors hover:bg-indigo-700"
                    >
                        <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add Task
                    </button>
                </div>

                {/* Statistics Cards */}
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-5">
                    <div className="overflow-hidden rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg">
                        <div className="p-6 text-white">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-blue-100">Total Tasks</p>
                                    <p className="mt-2 text-3xl font-bold">{stats.total_tasks}</p>
                                </div>
                                <div className="rounded-full bg-white/20 p-3">
                                    <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                                        />
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="overflow-hidden rounded-xl bg-gradient-to-br from-green-500 to-green-600 shadow-lg">
                        <div className="p-6 text-white">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-green-100">Total Hours</p>
                                    <p className="mt-2 text-3xl font-bold">{stats.total_hours.toFixed(1)}</p>
                                </div>
                                <div className="rounded-full bg-white/20 p-3">
                                    <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                        />
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="overflow-hidden rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 shadow-lg">
                        <div className="p-6 text-white">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-purple-100">Avg Hours/Task</p>
                                    <p className="mt-2 text-3xl font-bold">{stats.avg_hours_per_task.toFixed(1)}</p>
                                </div>
                                <div className="rounded-full bg-white/20 p-3">
                                    <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                                        />
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="overflow-hidden rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 shadow-lg">
                        <div className="p-6 text-white">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-orange-100">This Month</p>
                                    <p className="mt-2 text-3xl font-bold">{stats.tasks_this_month}</p>
                                </div>
                                <div className="rounded-full bg-white/20 p-3">
                                    <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                        />
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="overflow-hidden rounded-xl bg-gradient-to-br from-pink-500 to-pink-600 shadow-lg">
                        <div className="p-6 text-white">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-pink-100">Month Hours</p>
                                    <p className="mt-2 text-3xl font-bold">{stats.hours_this_month.toFixed(1)}</p>
                                </div>
                                <div className="rounded-full bg-white/20 p-3">
                                    <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                                        />
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Charts */}
                <div className="grid grid-cols-1 gap-6">
                    {/* Monthly Trend Chart */}
                    <div className="rounded-xl bg-white p-6 shadow-md">
                        <h2 className="mb-4 text-lg font-bold text-gray-900">Monthly Trend</h2>
                        {monthlyStats.length === 0 || monthlyStats.every(m => m.task_count === 0) ? (
                            <div className="flex h-[300px] items-center justify-center text-gray-500">
                                <div className="text-center">
                                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                                    </svg>
                                    <p className="mt-2">No monthly data yet</p>
                                    <p className="text-sm">Add tasks to see the trend</p>
                                </div>
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height={300}>
                                <LineChart data={monthlyStats}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                                    <XAxis dataKey="month" stroke="#6B7280" />
                                    <YAxis yAxisId="left" stroke="#8B5CF6" label={{ value: 'Tasks', angle: -90, position: 'insideLeft' }} />
                                    <YAxis yAxisId="right" orientation="right" stroke="#10B981" label={{ value: 'Hours', angle: 90, position: 'insideRight' }} />
                                    <Tooltip 
                                        contentStyle={{ backgroundColor: '#FFF', border: '1px solid #E5E7EB', borderRadius: '8px' }}
                                    />
                                    <Legend />
                                    <Line
                                        yAxisId="left"
                                        type="monotone"
                                        dataKey="task_count"
                                        stroke="#8B5CF6"
                                        strokeWidth={3}
                                        name="Tasks"
                                        dot={{ fill: '#8B5CF6', r: 4 }}
                                        activeDot={{ r: 6 }}
                                    />
                                    <Line
                                        yAxisId="right"
                                        type="monotone"
                                        dataKey="total_hours"
                                        stroke="#10B981"
                                        strokeWidth={3}
                                        name="Hours"
                                        dot={{ fill: '#10B981', r: 4 }}
                                        activeDot={{ r: 6 }}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                    
                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                        {/* Category Bar Chart */}
                        <div className="rounded-xl bg-white p-6 shadow-md">
                            <h2 className="mb-4 text-lg font-bold text-gray-900">Tasks & Hours by Category</h2>
                            {categoryData.length === 0 ? (
                                <div className="flex h-[300px] items-center justify-center text-gray-500">
                                    <div className="text-center">
                                        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                        </svg>
                                        <p className="mt-2">No data available</p>
                                        <p className="text-sm">Add tasks to see the distribution</p>
                                    </div>
                                </div>
                            ) : (
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={categoryData}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                                        <XAxis dataKey="name" stroke="#6B7280" angle={-45} textAnchor="end" height={80} />
                                        <YAxis stroke="#6B7280" />
                                        <Tooltip 
                                            contentStyle={{ backgroundColor: '#FFF', border: '1px solid #E5E7EB', borderRadius: '8px' }}
                                        />
                                        <Legend />
                                        <Bar dataKey="tasks" fill="#8B5CF6" name="Tasks" radius={[8, 8, 0, 0]} />
                                        <Bar dataKey="hours" fill="#10B981" name="Hours" radius={[8, 8, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </div>

                    {/* Category Distribution */}
                    <div className="rounded-xl bg-white p-6 shadow-md">
                        <h2 className="mb-4 text-lg font-bold text-gray-900">Hours by Category</h2>
                        {categoryData.length === 0 ? (
                            <div className="flex h-[300px] items-center justify-center text-gray-500">
                                <div className="text-center">
                                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                    <p className="mt-2">No data available</p>
                                    <p className="text-sm">Add tasks to see the distribution</p>
                                </div>
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                    <Pie
                                        data={categoryData}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={true}
                                        label={({ name, hours, percent }) => `${name}: ${hours}h (${(percent * 100).toFixed(0)}%)`}
                                        outerRadius={90}
                                        fill="#8884d8"
                                        dataKey="hours"
                                    >
                                        {categoryData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip 
									formatter={(value, name) => [`${Number(value).toFixed(2)} hours`, name]}
                                    />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="rounded-xl bg-white p-6 shadow-md">
                    <h2 className="mb-4 text-lg font-bold text-gray-900">Filters</h2>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                        <div>
                            <label className="mb-1 block text-sm font-medium text-gray-700">Category</label>
                            <select
                                value={selectedCategory}
                                onChange={e => setSelectedCategory(e.target.value)}
                                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            >
                                <option value="">All Categories</option>
                                {taskCategories.map(cat => (
                                    <option key={cat.name} value={cat.name}>
                                        {cat.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="mb-1 block text-sm font-medium text-gray-700">Start Date</label>
                            <input
                                type="date"
                                value={startDate}
                                onChange={e => setStartDate(e.target.value)}
                                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            />
                        </div>
                        <div>
                            <label className="mb-1 block text-sm font-medium text-gray-700">End Date</label>
                            <input
                                type="date"
                                value={endDate}
                                onChange={e => setEndDate(e.target.value)}
                                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            />
                        </div>
                        <div className="flex items-end">
                            <button
                                onClick={() => {
                                    setSelectedCategory('');
                                    setStartDate('');
                                    setEndDate('');
                                }}
                                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                            >
                                Clear Filters
                            </button>
                        </div>
                    </div>
                </div>

                {/* Task List */}
                <div className="rounded-xl bg-white shadow-md">
                    <div className="border-b border-gray-200 px-6 py-4">
                        <h2 className="text-lg font-bold text-gray-900">Task List</h2>
                    </div>
                    <div className="p-6">
                        {loading ? (
                            <div className="py-12 text-center text-gray-500">Loading tasks...</div>
                        ) : tasks.length === 0 ? (
                            <div className="py-12 text-center text-gray-500">
                                No tasks found. Click "Add Task" to get started!
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {tasks.map(task => (
                                    <div
                                        key={task.id}
                                        className="flex items-start justify-between rounded-lg border border-gray-200 p-4 transition-shadow hover:shadow-md"
                                        style={{ borderLeftWidth: '4px', borderLeftColor: getCategoryColor(task.category) }}
                                    >
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3">
                                                <h3 className="text-lg font-semibold text-gray-900">{task.subject}</h3>
                                                <span
                                                    className="rounded-full px-3 py-1 text-xs font-medium text-white"
                                                    style={{ backgroundColor: getCategoryColor(task.category) }}
                                                >
                                                    {task.category}
                                                </span>
                                            </div>
                                            {task.description && (
                                                <p className="mt-1 text-sm text-gray-600">{task.description}</p>
                                            )}
                                            <div className="mt-2 flex flex-wrap gap-4 text-sm text-gray-500">
                                                <span className="flex items-center">
                                                    <svg className="mr-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                                        />
                                                    </svg>
                                                    {new Date(task.date).toLocaleDateString()}
                                                </span>
                                                <span className="flex items-center">
                                                    <svg className="mr-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                                        />
                                                    </svg>
                                                    {task.start_time} - {task.end_time}
                                                </span>
                                                <span className="flex items-center font-semibold text-indigo-600">
                                                    <svg className="mr-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                                                        />
                                                    </svg>
                                                    {parseFloat(task.duration.toString()).toFixed(2)} hours
                                                </span>
                                            </div>
                                        </div>
                                        <div className="ml-4 flex gap-2">
                                            <button
                                                onClick={() => handleEdit(task)}
                                                className="rounded-lg bg-indigo-50 p-2 text-indigo-600 transition-colors hover:bg-indigo-100"
                                            >
                                                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                                    />
                                                </svg>
                                            </button>
                                            <button
                                                onClick={() => handleDelete(task.id)}
                                                className="rounded-lg bg-red-50 p-2 text-red-600 transition-colors hover:bg-red-100"
                                            >
                                                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                                    />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Task Form Modal */}
            {showForm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
                    <div className="w-full max-w-2xl rounded-xl bg-white shadow-2xl">
                        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                            <h2 className="text-xl font-bold text-gray-900">
                                {editingTask ? 'Edit Task' : 'Add New Task'}
                            </h2>
                            <button
                                onClick={() => {
                                    setShowForm(false);
                                    setEditingTask(null);
                                    resetForm();
                                }}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="p-6">
                            <div className="space-y-4">
                                <div>
                                    <label className="mb-1 block text-sm font-medium text-gray-700">Subject *</label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.subject}
                                        onChange={e => setFormData({ ...formData, subject: e.target.value })}
                                        className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                        placeholder="e.g., Department Meeting"
                                    />
                                </div>

                                <div>
                                    <label className="mb-1 block text-sm font-medium text-gray-700">Description</label>
                                    <textarea
                                        value={formData.description}
                                        onChange={e => setFormData({ ...formData, description: e.target.value })}
                                        rows={3}
                                        className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                        placeholder="Additional details about the task..."
                                    />
                                </div>

                                <div>
                                    <label className="mb-1 block text-sm font-medium text-gray-700">Category *</label>
                                    <select
                                        required
                                        value={formData.category}
                                        onChange={e => setFormData({ ...formData, category: e.target.value })}
                                        className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                    >
                                        <option value="">Select a category</option>
                                        {taskCategories.map(cat => (
                                            <option key={cat.name} value={cat.name}>
                                                {cat.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                                    <div>
                                        <label className="mb-1 block text-sm font-medium text-gray-700">Date *</label>
                                        <input
                                            type="date"
                                            required
                                            value={formData.date}
                                            onChange={e => setFormData({ ...formData, date: e.target.value })}
                                            className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="mb-1 block text-sm font-medium text-gray-700">Start Time *</label>
                                        <input
                                            type="time"
                                            required
                                            value={formData.start_time}
                                            onChange={e => setFormData({ ...formData, start_time: e.target.value })}
                                            className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="mb-1 block text-sm font-medium text-gray-700">End Time *</label>
                                        <input
                                            type="time"
                                            required
                                            value={formData.end_time}
                                            onChange={e => setFormData({ ...formData, end_time: e.target.value })}
                                            className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="mt-6 flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowForm(false);
                                        setEditingTask(null);
                                        resetForm();
                                    }}
                                    className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-md transition-colors hover:bg-indigo-700"
                                >
                                    {editingTask ? 'Update Task' : 'Create Task'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </LecturerLayout>
    );
}
